/* lib/api/utm.js — клиент модуля «UTM-метки» (раздел «Маркетинг» в консоли).
 * Раздел НЕ привязан к пространству — X-Workspace-Id бэком игнорируется, как в CRM.
 * Все функции unwrap'ят {ok,status,data} → бросают err{status,code,detail}
 * по контракту fakeApi, чтобы useFakeQuery и catch-ветки мутаций работали.
 * Исключения — публичный трекинг перехода (см. trackUtmHit), подсказки ссылок на тарифы
 * и курсы (см. getTagPlanLinks, getTagCourseLinks) и список ссылок метки (getTagLinks,
 * addTagLink, removeTagLink): они отдают конверт как есть. */

import { apiFetch } from './client.js';
import { unwrap } from './unwrap.js';

/* Query собираем ТОЛЬКО по белому списку ключей (как в crm.js). Экраны держат в
 * своём состоянии и служебное — nonce перечитывания, флаги открытых модалок; без
 * фильтра такой мусор уехал бы в адрес запроса. Пустая строка и false — это
 * «фильтр не выбран», а не значение: их не отправляем, иначе `?archived=false`
 * заставил бы бэк различать «не спрашивали» и «спросили про неархивные». */
function query(params, allowed) {
  const q = new URLSearchParams();
  for (const k of allowed) {
    const v = params[k];
    if (v != null && v !== '' && v !== false) q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

/* Список из ответа. Часть эндпоинтов отдаёт голый массив (типы), часть —
 * конверт с пагинацией (привлечённые). Читаем оба вида одной функцией: экран
 * иначе показал бы пустое состояние вместо данных, и понять это можно было бы
 * только по вкладке «Сеть». */
export function listOf(data, key) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  const box = (key && data[key]) || data.results || data.items;
  return Array.isArray(box) ? box : [];
}

// ── Справочник типов (каналов) ────────────────────────────────────────────
// Читает любой носитель права раздела (выбор канала в форме метки); заводит и
// правит администратор UTM (utm.manage_utm_types) на экране «Типы меток».
// Строка: {id, name, slug, is_active, tag_count}.
export const listUtmTypes = () => unwrap(apiFetch('/api/utm/types/'));

/* slug необязателен: пустой — бэкенд соберёт его из названия транслитерацией и
 * при занятости добавит числовой хвост. Заданный руками занятый код — ошибка
 * `slug_taken`, кривой — `bad_slug`; название занято — `name_taken`. */
export const createUtmType = ({ name, slug = '', is_active = true }) =>
  unwrap(apiFetch('/api/utm/types/', {
    method: 'POST', body: { name, slug, is_active },
  }));

/* Правка типа — ТОЛЬКО название и включённость. Кода в теле нет намеренно, и
 * лишние ключи сюда не пролезают: по коду собираются внешние сводки, и менять
 * его задним числом бэкенд не даёт (PATCH его молча игнорирует). */
export const updateUtmType = (id, { name, is_active } = {}) => {
  const body = {};
  if (name != null) body.name = name;
  if (is_active != null) body.is_active = is_active;
  return unwrap(apiFetch(`/api/utm/types/${id}/`, { method: 'PATCH', body }));
};

/* Период — общий параметр ЧИТАЮЩИХ эндпоинтов раздела: `from` и `to`, ISO-даты
 * включительно. Обе границы НЕОБЯЗАТЕЛЬНЫ, и это несущая часть контракта, а не
 * послабление: отсутствие границы означает «без ограничения с этой стороны»,
 * то есть накопительные числа за всё время — ровно то поведение, которое
 * раздел показывает на проде с самого запуска. Поэтому пресет «Всё время» не
 * шлёт ни from, ни to (а не подставляет далёкое прошлое): числа обязаны
 * остаться байт-в-байт прежними, иначе владелец завтра увидит «упавшие»
 * регистрации без единого объяснения.
 * Пустые значения отсекает сам `query` — undefined в параметрах безопасен. */
const PERIOD_KEYS = ['from', 'to'];

/* Окно уходит в запрос ЦЕЛИКОМ или не уходит вовсе. Половина окна («только
 * from») означала бы, что бэкенд честно режет числа по одной границе, а
 * страница подписывает их «За всё время» — число с чужой подписью, тот самый
 * дефект, ради которого раздел и правился. Экран это правило уже соблюдает
 * (`resolvePeriod`), но здесь оно повторено намеренно: клиент API — общая
 * дверь, и следующий вызывающий не обязан знать про подписи на экране.
 *
 * Рядов графика правило НЕ касается (см. getUtmTagSeries): у них верхняя
 * граница осмысленна и без нижней — бэкенд сам достраивает окно по
 * гранулярности, и это его штатное поведение, а не молчаливая обрезка. */
const wholeWindowOnly = (params) =>
  (params.from && params.to ? params : { ...params, from: undefined, to: undefined });

// ── Метки ─────────────────────────────────────────────────────────────────
// params: {type, q, archived, owner, from, to} — archived=true показывает метки
// в архиве; owner='me' — только свои (фильтр администратора: сотруднику бэкенд
// и так отдаёт одни его метки); from/to сужают СЧЁТЧИКИ строк, но не сам список.
export const listUtmTags = (params = {}) =>
  unwrap(apiFetch(`/api/utm/tags/${query(wholeWindowOnly(params), ['type', 'q', 'archived', 'owner', ...PERIOD_KEYS])}`));

/* amocrm_responsible_user_id — id менеджера AmoCRM, которому уходят заявки с
 * демо-стенда по этой ссылке; 0 (или не передан) — общее распределение по кругу.
 * demo_days — партнёрское демо, дней. Ключ уходит, ТОЛЬКО если передан: его
 * появление от не-администратора бэкенд отвергает 403 даже с пустым значением. */
export const createUtmTag = ({ name, type, amocrm_responsible_user_id = 0, demo_days }) =>
  unwrap(apiFetch('/api/utm/tags/', {
    method: 'POST',
    body: { name, type, amocrm_responsible_user_id, ...(demo_days !== undefined ? { demo_days } : {}) },
  }));

/* Менеджеры AmoCRM для выбора ответственного. Ответ — КОНВЕРТ
 * {available, users, reason} и всегда 200: форма метки не имеет права зависеть
 * от доступности чужого CRM, при available=false выбор просто прячется. */
export const listAmocrmUsers = () => unwrap(apiFetch('/api/utm/amocrm-users/'));

/* params: {from, to} — воронка, конверсии и выручка за выбранный период.
 * Ответ несёт ПРИМЕНЁННОЕ окно (`period`), и экран подписывает числа именно
 * им, а не состоянием селектора: иначе заголовок перерисовывается сразу, а
 * числа приезжают позже — и на секунду виден кадр «Июль 2026» над
 * накопительной суммой. Поле из ответа не выбрасывать и не «нормализовать». */
export const getUtmTag = (id, params = {}) =>
  unwrap(apiFetch(`/api/utm/tags/${id}/${query(wholeWindowOnly(params), PERIOD_KEYS)}`));

// body: {name?, is_active?} — архивация меток вместо удаления.
export const updateUtmTag = (id, body) =>
  unwrap(apiFetch(`/api/utm/tags/${id}/`, { method: 'PATCH', body }));

// ── Ряды для графика: granularity=day|week|month, from/to — ISO-даты ──────
export const getUtmTagSeries = (id, params = {}) =>
  unwrap(apiFetch(`/api/utm/tags/${id}/series/${query(params, ['granularity', ...PERIOD_KEYS])}`));

/* ── Привлечённые пользователи (ПДн: эндпоинт под правом manage_utm_tags) ──
 * В окно попадает всякий, у кого в нём было ХОТЬ ЧТО-ТО: привязка метки, оплата
 * или продление. Отбор ИМЕННО такой, а не «по дате привязки»: список обязан
 * объяснять числа воронки. При отборе по привязке партнёр, приведший десять
 * человек в июле и получивший три оплаты в августе, видел «Оплаты: 3» рядом с
 * пустым списком — и не мог понять, кто их принёс. Чем именно человек попал в
 * окно, говорит поле `in_period` в строке.
 * Пагинация считается внутри того же окна, поэтому при смене периода экран
 * обязан начать листать с нуля — иначе к списку одного периода допишется
 * страница другого. */
export const listUtmTagUsers = (id, params = {}) =>
  unwrap(apiFetch(`/api/utm/tags/${id}/users/${query(wholeWindowOnly(params), ['limit', 'offset', ...PERIOD_KEYS])}`));

/* ── Ссылки метки на покупку тарифа (контракт contract-w1-utm-plan-links) ──
 * Список тарифов, которые сейчас можно продать по ссылке, с ГОТОВЫМ адресом
 * `url` у каждого срока. Адрес собирает бэкенд из своих настроек (`link_base`) —
 * фронт хостов не склеивает: стоит ему завести свою базу, и ссылка уедет на
 * домен, где куки устройства нет (см. комментарий у linkOf на экране меток).
 * БЕЗ unwrap, по контракту: ответ — конверт {ok, status, data}. Строка тарифов —
 * лишь часть карточки метки, и её отказ (403 при выключенном разделе, сбой)
 * обязан остаться отказом этой строки, а не исключением, роняющим карточку. */
export const getTagPlanLinks = (tagId) => apiFetch(`/api/utm/tags/${tagId}/plan-links/`);

const AUDIENCE_LABELS = { personal: 'Личные (B2C)', business: 'Бизнес (B2B)' };

// Decimal приходит строкой ('27900.00'); пустое и мусор — null, а не 0:
// нулевая цена в подписи читалась бы как «бесплатно».
const numOrNull = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/* Ответ plan-links в виде, который выбор читает без проверок на каждом шаге.
 * Ключи — те же, что в контракте (snake_case), чтобы сверка «сериализатор отдаёт
 * ровно эти ключи — клиент читает ровно их» шла по одному словарю. Недостающее
 * поле не роняет выбор:
 *  - нет подписи группы — подпись по аудитории; нет имени семейства — имя
 *    первого срока, затем слаг: строка выбора не остаётся безымянной;
 *  - нет `months` — считаем, как бэкенд, max(1, round(period_days / 30));
 *  - нет `is_promo` — выводим тем же правилом, что бэкенд (окно продаж или
 *    «начинается после текущего»);
 *  - нет `on_sale` — null («сервер не сказал»), см. isUpcoming в screens/utm/planLinks.js;
 *  - срок без `url` выбрасываем: показать продавцу нечего, а собрать адрес
 *    самим нельзя (см. выше). Опустевшие семейства и группы уходят следом.
 * Порядок НЕ меняем: его задаёт бэкенд (контракт, `ordering`), и вторая
 * сортировка здесь разошлась бы с ним на первом же новом тарифе. */
export function planLinksOf(data) {
  const box = data && typeof data === 'object' ? data : {};
  const rawGroups = Array.isArray(box.groups) ? box.groups : [];
  const groups = rawGroups.map((g) => {
    const grp = g && typeof g === 'object' ? g : {};
    const rawFamilies = Array.isArray(grp.families) ? grp.families : [];
    const families = rawFamilies.map((f) => {
      const fam = f && typeof f === 'object' ? f : {};
      const rawOptions = Array.isArray(fam.options) ? fam.options : [];
      const options = rawOptions
        .filter((o) => o && typeof o === 'object' && o.slug && o.url)
        .map((o) => {
          const periodDays = numOrNull(o.period_days);
          const months = numOrNull(o.months);
          const startsAfter = !!o.starts_after_current;
          const startsAt = o.sale_starts_at || null;
          const endsAt = o.sale_ends_at || null;
          return {
            slug: String(o.slug),
            name: o.name ? String(o.name) : '',
            period_days: periodDays,
            months: months && months > 0
              ? months
              : (periodDays ? Math.max(1, Math.round(periodDays / 30)) : null),
            price_rub: numOrNull(o.price_rub),
            per_month_rub: numOrNull(o.per_month_rub),
            discount_pct: numOrNull(o.discount_pct),
            is_promo: o.is_promo != null ? !!o.is_promo : !!(startsAfter || startsAt || endsAt),
            starts_after_current: startsAfter,
            sale_starts_at: startsAt,
            sale_ends_at: endsAt,
            // Продаётся ли тариф прямо сейчас — слово СЕРВЕРА (его часы, не
            // браузера продавца): по нему подпись выбирает «продажи с …» или
            // «акция до …». Не прислал — null, а не выдуманное «в продаже»: тогда
            // и только тогда подпись решает по часам браузера. Прятать тариф
            // молчание поля не повод — фильтра по нему нет.
            on_sale: typeof o.on_sale === 'boolean' ? o.on_sale : null,
            url: String(o.url),
          };
        });
      const first = options[0] || {};
      return {
        key: fam.key ? String(fam.key) : (first.slug || ''),
        name: fam.name ? String(fam.name) : (first.name || first.slug || ''),
        is_promo: !!fam.is_promo,
        options,
      };
    }).filter((f) => f.options.length > 0);
    const audience = grp.audience ? String(grp.audience) : '';
    return {
      audience,
      label: grp.label ? String(grp.label) : (AUDIENCE_LABELS[audience] || audience),
      families,
    };
  }).filter((g) => g.families.length > 0);
  return { tag_id: box.tag_id != null ? box.tag_id : null, groups };
}

/* ── Ссылки метки на страницы курсов Академии (контракт contract-w1b-utm-course-links) ──
 * Опубликованные курсы с публичной страницей и ГОТОВЫЙ адрес `url` у каждого —
 * `/r/<код>/course/<слаг в процентах>`, собранный бэкендом (хостов фронт не
 * склеивает — см. getTagPlanLinks). Академия выключена или курсов в публикации
 * нет — 200 с `courses: []`, и строка ссылки прячется целиком.
 * БЕЗ unwrap, по той же причине, что у тарифов: отказ этой строки не роняет карточку. */
export const getTagCourseLinks = (tagId) => apiFetch(`/api/utm/tags/${tagId}/course-links/`);

/* Ответ course-links в виде, который строка выбора читает без проверок. Ключи —
 * контракта (snake_case), как у planLinksOf: сверка «бэкенд отдаёт ровно эти
 * ключи — клиент читает ровно их» идёт по одному словарю.
 *  - курс без `slug` или `url` выбрасываем: собрать адрес самим нельзя, а без слага
 *    нечем привязать выбор и «Скопировано»;
 *  - нет названия — подпись слагом: строка выбора не остаётся безымянной;
 *  - цена — число; '0.00' — честный ноль («бесплатно»), мусор — null (подпись цену
 *    опустит): нулём он читался бы как «бесплатно» по платному курсу;
 *  - `start_on_date` — строка как пришла или null; разбирает её подпись.
 * Слаг НЕ трогаем (ни регистр, ни кодировку): он может быть кириллическим, и выбор,
 * и ключ подтверждения живут ровно той строкой, что хранит бэкенд.
 * Порядок — бэкенда (контракт: order, затем title), своей сортировки нет. */
export function courseLinksOf(data) {
  const box = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
  const raw = Array.isArray(box.courses) ? box.courses : [];
  const courses = raw
    .filter((c) => c && typeof c === 'object' && c.slug && c.url)
    .map((c) => ({
      slug: String(c.slug),
      title: c.title ? String(c.title) : String(c.slug),
      price_rub: numOrNull(c.price_rub),
      price_from: c.price_from === true,
      start_on_date: c.start_on_date ? String(c.start_on_date) : null,
      url: String(c.url),
    }));
  return { tag_id: box.tag_id != null ? box.tag_id : null, courses };
}

/* Курсовая часть воронки метки: три ДОБАВОЧНЫХ ключа контракта в `funnel`. Здесь,
 * в отличие от счётчиков тарифной воронки, недостающее — ноль, а не прочерк:
 * строки курсов показываются только там, где Академия точно включена (см.
 * courseLinesShown), а открытая до релиза вкладка читает ответ без этих ключей —
 * «undefined покупок» вместо «0 покупок» было бы поломкой на ровном месте. */
export function courseFunnelOf(funnel) {
  const f = funnel && typeof funnel === 'object' ? funnel : {};
  const n = (v) => numOrNull(v) || 0;
  return {
    course_purchases: n(f.course_purchases),
    course_revenue: n(f.course_revenue),
    course_leads: n(f.course_leads),
  };
}

/* ── Ссылки метки — сохранённый список (контракт contract-w1c-utm-link-builder) ──
 * У метки свой список ссылок: «Регистрация» есть всегда (это сама ссылка метки), остальные
 * продавец добавляет из подсказок (тарифы, курсы, лендинг, демо) и убирает лишние. Список
 * хранится на метке и виден всем, кто видит метку; убранная ссылка лишь пропадает из
 * списка, а сам адрес `/r/…` продолжает работать — его могли уже разослать.
 * БЕЗ unwrap, как у тарифов и курсов: отказ списка — отказ этого блока карточки, а не
 * исключение, роняющее карточку; потоки добавления и удаления читают конверт сами. */
export const getTagLinks = (tagId) => apiFetch(`/api/utm/tags/${tagId}/links/`);

/* target у лендинга и демо — пустая строка по контракту (не null и не undefined: бэкенд
 * сверяет уникальность по паре kind+target, и «нет значения» должно быть одним). Слаг
 * курса может быть кириллическим — уходит как есть, кодирует его JSON. */
export const addTagLink = (tagId, { kind, target = '' } = {}) =>
  apiFetch(`/api/utm/tags/${tagId}/links/`, {
    method: 'POST', body: { kind, target: target == null ? '' : String(target) },
  });

export const removeTagLink = (tagId, id) =>
  apiFetch(`/api/utm/tags/${tagId}/links/${id}/`, { method: 'DELETE' });

export const TAG_LINK_KINDS = ['signup', 'site', 'demo', 'buy', 'course'];
const TAG_LINK_TITLES = { signup: 'Регистрация', site: 'Лендинг', demo: 'Демо-стенд' };

/* Одна строка списка — ровно ключи контракта. Недостающее не роняет список:
 *  - без адреса или с неизвестным видом строку выбрасываем: показать и скопировать нечего;
 *  - нет подписи — подпись по виду («Лендинг»), у тарифа и курса — слагом;
 *  - нет `available` — считаем доступной: пометка «не продаётся» без слова сервера
 *    пугала бы продавца зря;
 *  - нет `hits_total` — null, а не 0 («не знаем» и «никто не перешёл» — разные ответы);
 *  - «Регистрацию» убрать нельзя никогда (A9) — removable=false и id=null при любом
 *    ответе; у остальных без id удалять нечем, без поля removable — решает наличие id. */
export function tagLinkOf(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const kind = raw.kind ? String(raw.kind) : '';
  if (!TAG_LINK_KINDS.includes(kind) || !raw.url) return null;
  const signup = kind === 'signup';
  const target = raw.target == null ? '' : String(raw.target);
  const id = signup || raw.id == null || raw.id === '' ? null : raw.id;
  return {
    id,
    kind,
    target,
    title: raw.title ? String(raw.title) : (TAG_LINK_TITLES[kind] || target || kind),
    subtitle: raw.subtitle ? String(raw.subtitle) : '',
    url: String(raw.url),
    available: raw.available !== false,
    hits_total: numOrNull(raw.hits_total),
    removable: !signup && id != null && raw.removable !== false,
  };
}

/* Ссылка «по другим» (`other_links`, A12): по ней переходили, а в списке её нет — её
 * разослали до появления списка или убрали из него, а адрес продолжает работать. Ключи
 * контракта — те же, что у строки списка, кроме id и removable: убирать нечего, её
 * можно только вернуть в список. «Регистрации» здесь не бывает (она в списке всегда) —
 * пришла по ошибке, отбрасываем. Недостающее — как у строки списка (tagLinkOf). */
export const OTHER_LINK_KINDS = ['site', 'demo', 'buy', 'course'];

export function otherLinkOf(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const kind = raw.kind ? String(raw.kind) : '';
  if (!OTHER_LINK_KINDS.includes(kind) || !raw.url) return null;
  const target = raw.target == null ? '' : String(raw.target);
  return {
    kind,
    target,
    title: raw.title ? String(raw.title) : (TAG_LINK_TITLES[kind] || target || kind),
    subtitle: raw.subtitle ? String(raw.subtitle) : '',
    url: String(raw.url),
    available: raw.available !== false,
    hits_total: numOrNull(raw.hits_total),
  };
}

/* Ответ links в виде, который список читает без проверок. «Регистрация» — первой (A9),
 * даже если сервер прислал её не первой; дубль пары kind+target отбрасываем (первый
 * побеждает): два одинаковых ряда с одним «Скопировано» на двоих путали бы продавца.
 * Остальной порядок — бэкенда (по времени добавления), своей сортировки нет.
 * `other_links` — по тому же правилу и порядку бэкенда (по убыванию переходов); ссылку,
 * которая уже стоит в списке, отбрасываем: в двух местах с разными счётчиками она
 * читалась бы как две разные. */
export function tagLinksOf(data) {
  const box = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
  const raw = Array.isArray(box.links) ? box.links : [];
  const seen = new Set();
  let signup = null;
  const rest = [];
  for (const item of raw) {
    const l = tagLinkOf(item);
    if (!l) continue;
    const key = `${l.kind}:${l.target}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (l.kind === 'signup') signup = l;
    else rest.push(l);
  }
  const others = [];
  for (const item of Array.isArray(box.other_links) ? box.other_links : []) {
    const o = otherLinkOf(item);
    if (!o) continue;
    const key = `${o.kind}:${o.target}`;
    if (seen.has(key)) continue;
    seen.add(key);
    others.push(o);
  }
  return {
    tag_id: box.tag_id != null ? box.tag_id : null,
    counted_since: box.counted_since ? String(box.counted_since) : null,
    links: signup ? [signup, ...rest] : rest,
    other_links: others,
  };
}

/* Фолбэк-трекинг перехода для роута /r/:code (когда в nginx нет `location ^~ /r/`).
 * БЕЗ unwrap: провал трекинга не должен превращаться в
 * исключение — экран перехода обязан увести человека дальше в любом случае,
 * а не показать ему ошибку трекинга. `to` — назначение ссылки (site/demo/buy/course);
 * `slug` — тариф ссылки «на покупку» (`/r/<код>/buy/<слаг>`) или курс ссылки «на
 * страницу курса» (`/r/<код>/course/<слаг>`), уходит только вместе с ними.
 * КУДА уводить, отвечает бэкенд полем `url` в `data` ответа: адрес лендинга живёт
 * в его настройках, а продаётся ли тариф и опубликован ли курс, знает только он
 * (снятый с продажи или с публикации — на регистрацию). apiFetch не бросает по
 * HTTP-кодам, так что вызывающему остаётся только страховка на отказ самого fetch.
 *
 * Тело — ОБЫЧНЫЙ ОБЪЕКТ, и это часть контракта, а не стиль. apiFetch шлёт
 * объект как JSON (`Content-Type: application/json`), а FormData — как
 * multipart-форму; эндпоинт же принимает ТОЛЬКО JSON (`parser_classes=[JSONParser]`
 * на бэке) и на форму ответит 415. Форма отсюда убрана не ради аккуратности:
 * «простой» запрос с формой браузер шлёт БЕЗ предварительного preflight, CORS
 * его не останавливает (он запрещает читать ответ, а не отправлять запрос), а
 * CSRF на публичном входе выключен вместе с аутентификацией — то есть чужая
 * страница могла бы накручивать переходы по чужой метке. JSON делает запрос
 * «непростым», браузер обязан сделать preflight, и кросс-сайтовый вызов не
 * доходит. Никаких FormData/URLSearchParams здесь — молча сломает и то, и другое. */
export const trackUtmHit = (code, to, slug) =>
  apiFetch('/api/utm/hit/', {
    method: 'POST', body: { code, to: to || undefined, slug: slug || undefined },
  });
